import styled from "styled-components";
import moment from "moment";
import { BsTelephone } from "react-icons/bs";
import { CardComp } from "..";
import InvestmentRequestDetailContent from "./InvestmentRequestDetailContent";
import InvestorStatusBadge from "./InvestorStatusBadge";
import { formatMoney } from "../../utils/currencyFormat";
import { convertToTitleCase } from "../../utils/helpers";

const TYPE_LABELS = {
  investment: "Investment",
  marketplace: "Marketplace order",
  cooperative: "Cooperative request",
  real_estate: "Property request",
};

const PageGrid = styled.div`
  display: grid;
  gap: 1.25rem;

  @media (min-width: 992px) {
    grid-template-columns: minmax(0, 1fr) minmax(280px, 320px);
    gap: 1.5rem 2rem;
    align-items: start;
  }
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.85rem 0;
  border-bottom: 1px solid #f1f1f1;
  font-size: 0.9rem;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  .label {
    color: #525252;
    flex-shrink: 0;
  }

  .value {
    color: #383838;
    font-weight: 600;
    text-align: right;
    word-break: break-word;
  }
`;

function GenericRequestDetail({ request }) {
  const payload = request.payload || {};

  return (
    <PageGrid>
      <div>
        <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
          <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
            <InvestorStatusBadge
              status={request.type}
              label={TYPE_LABELS[request.type] || convertToTitleCase(request.type)}
              backgroundColor="var(--color-secondary)"
            />
            <InvestorStatusBadge status={request.status || "Submitted"} />
          </div>
          <h1 className="h5 fw-bold mb-2" style={{ color: "#383838" }}>
            {payload.title || payload.name || "Request"}
          </h1>
          <p className="text-muted small mb-0">
            Reference <strong>{request.id}</strong> · {moment(request.created_at).format("DD MMM YYYY, HH:mm")}
          </p>
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm rounded-4 mb-3">
          <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
            Summary
          </h2>
          {payload.quantity != null ? (
            <DetailRow>
              <span className="label">Quantity</span>
              <span className="value">
                {payload.quantity}
                {payload.unit ? ` · ${payload.unit}` : ""}
              </span>
            </DetailRow>
          ) : null}
          {payload.totalNgn != null ? (
            <DetailRow>
              <span className="label">Total amount</span>
              <span className="value" style={{ color: "var(--color-primary)" }}>
                {formatMoney(payload.totalNgn)}
              </span>
            </DetailRow>
          ) : null}
          {payload.estimatedValueNgn != null ? (
            <DetailRow>
              <span className="label">Estimated value</span>
              <span className="value">{formatMoney(payload.estimatedValueNgn)}</span>
            </DetailRow>
          ) : null}
          {payload.deliveryAddress ? (
            <DetailRow>
              <span className="label">Delivery address</span>
              <span className="value">{payload.deliveryAddress}</span>
            </DetailRow>
          ) : null}
          {payload.cityState ? (
            <DetailRow>
              <span className="label">City / state</span>
              <span className="value">{payload.cityState}</span>
            </DetailRow>
          ) : null}
          {payload.note ? (
            <div className="pt-3 mt-1 border-top">
              <p className="small text-muted mb-1">Note</p>
              <p className="mb-0" style={{ lineHeight: 1.6, color: "#45464e" }}>
                {payload.note}
              </p>
            </div>
          ) : null}
        </CardComp>

        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          <h2 className="h6 fw-bold mb-3" style={{ color: "#383838" }}>
            Contact
          </h2>
          <DetailRow>
            <span className="label">Full name</span>
            <span className="value">{payload.buyerName || payload.full_name || payload.investorName || "—"}</span>
          </DetailRow>
          {payload.phone ? (
            <DetailRow>
              <span className="label">Phone</span>
              <span className="value">{payload.phone}</span>
            </DetailRow>
          ) : null}
          {payload.contact_method ? (
            <DetailRow>
              <span className="label">Preferred contact</span>
              <span className="value">{payload.contact_method}</span>
            </DetailRow>
          ) : null}
        </CardComp>
      </div>

      <aside>
        <CardComp cardClassName="border-0 shadow-sm rounded-4">
          <h2 className="h6 fw-bold mb-2" style={{ color: "#383838" }}>
            What happens next?
          </h2>
          <p className="text-muted small mb-3" style={{ lineHeight: 1.6 }}>
            Our team will review your submission and contact you with next steps. Track all requests from My Requests.
          </p>
          {payload.phone ? (
            <div className="d-flex align-items-center gap-2 small text-muted mb-3">
              <BsTelephone aria-hidden />
              <span>We may call {payload.phone}</span>
            </div>
          ) : null}
          {payload.productId ? (
            <p className="text-muted small mb-0">Product reference: {payload.productId}</p>
          ) : null}
        </CardComp>
      </aside>
    </PageGrid>
  );
}

export default function InvestorRequestDetailContent({ request }) {
  if (!request) return null;
  if (request.type === "investment") {
    return <InvestmentRequestDetailContent request={request} />;
  }
  return <GenericRequestDetail request={request} />;
}
